import { graphql, useStaticQuery } from 'gatsby';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import React from 'react';
import FadeInAnimationWrapper from 'src/elements/animation/fade-in/FadeInAnimationWrapper';
import Image from 'src/elements/image/image';
import LinkBranded from 'src/elements/links/Link.branded';
import LinkSimple from 'src/elements/links/Link.simple';
import Heading from 'src/elements/typography/heading';
import Paragraph from 'src/elements/typography/paragraph';
import { CulturalFitAction, OurTeamConfettiContainer, OurTeamContainer, OurTeamCultureFeatText, OurTeamImage, OurTeamImageMobile, OurTeamText } from './ourTeam.styles';

interface OurTeamProps {
  title: string;
  subtitle: string;
  showCulturalFit: boolean;
  ourTeamImage?: IGatsbyImageData;
  ourTeamMobileImage?: IGatsbyImageData;
  buttonText?: string;
  variant: 'careers' | 'about';
}

const OurTeam = ({
  title,
  subtitle,
  showCulturalFit,
  variant,
  ourTeamImage,
  ourTeamMobileImage,
  buttonText
}: OurTeamProps): JSX.Element => {
  const image: any = useStaticQuery(graphql`{
    mightyteam: contentfulAsset(contentful_id: {eq: "4OuhWPHk2UFBPlRxnpQPdn"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
    mightyteamMobile: contentfulAsset(contentful_id: {eq: "74nR1yHm3EIpC7pRylEQ2f"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
  }
  `)

  return (
    <OurTeamContainer>
      <OurTeamConfettiContainer variant={variant}>
        <FadeInAnimationWrapper>
          {
            showCulturalFit ?
              <Heading
                text={title}
                size="2.5em"
                sizeLg="2em"
                sizeSubLg="1.5em"
                align="center"
              />
              :
              <Heading
                text={title}
                size="3.375em"
                sizeLg="2.5em"
                sizeSm="2em"
                sizeXs="1.5em"
                align="center"
              />
          }
        </FadeInAnimationWrapper>
        <FadeInAnimationWrapper style={{ display: 'grid', justifySelf: 'center' }}>
          <OurTeamText spaced={!showCulturalFit}>
            <Paragraph
              text={subtitle}
              align="center"
              size="1.5em"
              sizeSubLg="1em"
              weight="light"
            />
            {
              showCulturalFit ?
                <LinkSimple
                  justify="center"
                  iconPosition="right"
                  icon={<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20.66 15.8" height="11px"><path d="M18.84,8.26A24.57,24.57,0,0,1,15,5.14,11.73,11.73,0,0,1,13.45,1.6l-2.9.8a15,15,0,0,0,2.17,4.65,11.65,11.65,0,0,0,.93,1H0v3H13.65a11.65,11.65,0,0,0-.93.95,15,15,0,0,0-2.17,4.65l2.9.8A12,12,0,0,1,15,13.86a24.73,24.73,0,0,1,3.82-3.12L20.66,9.5Z" transform="translate(0 -1.6)"></path></svg>}
                  text="See Open Positions"
                  url="/careers"
                  smooth={false}
                  color="main"
                  size="1.4em"
                  sizeSubLg="1em"
                />
                :
                <LinkSimple
                  justify="center"
                  iconPosition="right"
                  icon={<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20.66 15.8" height="11px"><path d="M18.84,8.26A24.57,24.57,0,0,1,15,5.14,11.73,11.73,0,0,1,13.45,1.6l-2.9.8a15,15,0,0,0,2.17,4.65,11.65,11.65,0,0,0,.93,1H0v3H13.65a11.65,11.65,0,0,0-.93.95,15,15,0,0,0-2.17,4.65l2.9.8A12,12,0,0,1,15,13.86a24.73,24.73,0,0,1,3.82-3.12L20.66,9.5Z" transform="translate(0 -1.6)"></path></svg>}
                  text={buttonText ?? 'See Open Positions'}
                  url="#jobs"
                  smooth={true}
                  color="main"
                  size="1.4em"
                  sizeSubLg="1em"
                />
            }
          </OurTeamText>
        </FadeInAnimationWrapper>
        {
          showCulturalFit ?
            <>
              <FadeInAnimationWrapper>
                <OurTeamCultureFeatText>
                  <Paragraph
                    text="We're not looking for a cultural fit, we're looking for a culture add."
                    align="center"
                    size="1.5em"
                    sizeSubLg="1em"
                    weight="light"
                  />
                </OurTeamCultureFeatText>
              </FadeInAnimationWrapper>
              <FadeInAnimationWrapper>
                <OurTeamText>
                  <Paragraph
                    text="Remote First"
                    align="center"
                    size="1.5em"
                    sizeSubLg="1em"
                    weight="light"
                  />
                  <Paragraph
                    text="Open collaboration"
                    align="center"
                    size="1.5em"
                    sizeSubLg="1em"
                    weight="light"
                  />
                  <Paragraph
                    text="Continuous learning"
                    align="center"
                    size="1.5em"
                    sizeSubLg="1em"
                    weight="light"
                  />
                  <Paragraph
                    text="Promote from within"
                    align="center"
                    size="1.5em"
                    sizeSubLg="1em"
                    weight="light"
                  />
                  <Paragraph
                    text="Optimize for focus, flexibility & flow"
                    align="center"
                    size="1.5em"
                    sizeSubLg="1em"
                    weight="light"
                  />
                </OurTeamText>
              </FadeInAnimationWrapper>
              <FadeInAnimationWrapper>
                <CulturalFitAction>
                  <LinkBranded
                    text="Join Our Team"
                    url="/careers"
                    justify="center"
                  />
                </CulturalFitAction>
              </FadeInAnimationWrapper>
            </>
            : null
        }
        <FadeInAnimationWrapper>
          <OurTeamImage>
            <Image childImageSharp={ourTeamImage ?? image.mightyteam.gatsbyImageData} alt="our team" />
          </OurTeamImage>
          <OurTeamImageMobile>
            <Image childImageSharp={ourTeamMobileImage ?? image.mightyteamMobile.gatsbyImageData} alt="our team" />
          </OurTeamImageMobile>
        </FadeInAnimationWrapper>
      </OurTeamConfettiContainer>
    </OurTeamContainer>
  )
}

export default OurTeam;
